/**
 * Passkey (WebAuthn) ceremonies and the API calls around them. Everything that touches
 * navigator.credentials lives here, so components only deal with promises and plain data.
 */

import axios from 'axios'
import { startAuthentication, startRegistration, browserSupportsWebAuthn, platformAuthenticatorIsAvailable, browserSupportsWebAuthnAutofill, WebAuthnAbortService } from '@simplewebauthn/browser'
import type { PublicKeyCredentialCreationOptionsJSON, PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser'
import type { LoginResponse } from '@/TcxApi/Authentication'
import type { WebauthnCredential, WebauthnOptionsResponse } from '@/TcxApi/Webauthn'

/**
 * Whether this browser can do WebAuthn at all.
 * @returns True when the API is present
 */
export function passkeysSupported() {
	return browserSupportsWebAuthn()
}

/**
 * Whether this device has a built in authenticator - Touch ID, Windows Hello or similar. In the macOS
 * Electron build this stays false until the wrapper has called app.configureWebAuthn().
 * @returns True when a platform authenticator is available
 */
export async function platformAuthenticatorAvailable() {
	try { return await platformAuthenticatorIsAvailable() }
	catch (_) { return false }
}

/**
 * Whether the passkey buttons in the login dialog should be offered at all. A browser needs nothing beyond
 * WebAuthn support - it brings its own UI for security keys, phones and password managers and reports a
 * missing credential at once. Inside the wrapper only a platform authenticator can serve a request; without
 * one Chromium silently waits for a USB key until the request times out, so the buttons are withheld.
 * @param embedded True when running inside the desktop wrapper
 * @returns True when a passkey login or enrollment can be attempted here
 */
export async function passkeyLoginAvailable(embedded: boolean) {
	if (!browserSupportsWebAuthn()) { return false }
	if (!embedded) { return true }

	return platformAuthenticatorAvailable()
}

/**
 * Whether the browser can show passkeys in its autofill dropdown (conditional UI). Used to decide
 * whether to start a conditional authentication alongside the password field.
 * @returns True when conditional UI is supported, false when unsupported or the check throws
 */
export async function passkeyAutofillSupported() {
	if (!browserSupportsWebAuthn()) {
		return false
	}

	try { return await browserSupportsWebAuthnAutofill() }
	catch (_) { return false }
}

/**
 * Cancels a pending WebAuthn ceremony, such as a conditional (autofill) authentication started by
 * passkeyLoginAutofill(). A password login is not itself a WebAuthn ceremony, so it does not replace
 * a pending conditional request - without cancelling it here, the browser's passkey sheet can keep
 * hanging over the 2-factor step. Safe to call when nothing is pending.
 */
export function cancelPendingPasskey() {
	try { WebAuthnAbortService.cancelCeremony() }
	catch (_) { /* Nothing pending - nothing to do */ }
}

/**
 * Signs in with a passkey.
 * @param apiUrl Base URL of the API
 * @param persist Ask for a persistent token
 * @returns The same payload /auth/login returns
 */
export async function passkeyLogin(apiUrl: string, persist: boolean) {
	const options = await axios.post<WebauthnOptionsResponse<PublicKeyCredentialRequestOptionsJSON>>(`${apiUrl}/auth/webauthn/authenticate/options`, {})
	const response = await timedCeremony(() => startAuthentication({ optionsJSON: options.data.options }))
	const result = await axios.post<LoginResponse>(`${apiUrl}/auth/webauthn/authenticate`, { challengeId: options.data.challengeId, response, persist })

	return result.data
}

/**
 * Starts a conditional authentication so a passkey appears in the browser's autofill dropdown. Resolves
 * only when the user picks one.
 * @param apiUrl Base URL of the API
 * @returns The same payload /auth/login returns
 */
export async function passkeyLoginAutofill(apiUrl: string) {
	const options = await axios.post<WebauthnOptionsResponse<PublicKeyCredentialRequestOptionsJSON>>(`${apiUrl}/auth/webauthn/authenticate/options`, {})
	const response = await startAuthentication({ optionsJSON: options.data.options, useBrowserAutofill: true })
	const result = await axios.post<LoginResponse>(`${apiUrl}/auth/webauthn/authenticate`, { challengeId: options.data.challengeId, response })

	return result.data
}

/**
 * Registers a passkey for the signed in employee.
 * @param apiUrl Base URL of the API
 * @param token Session token
 * @param name Label for the new passkey
 * @returns The stored credential
 */
export async function passkeyRegister(apiUrl: string, token: string, name?: string) {
	const headers = { Authorization: `Bearer ${token}` }
	const options = await axios.post<WebauthnOptionsResponse<PublicKeyCredentialCreationOptionsJSON>>(`${apiUrl}/auth/webauthn/register/options`, {}, { headers })
	const response = await startRegistration({ optionsJSON: options.data.options })
	const body = name ? { challengeId: options.data.challengeId, response, name } : { challengeId: options.data.challengeId, response }
	const result = await axios.post<{ credential: WebauthnCredential }>(`${apiUrl}/auth/webauthn/register`, body, { headers })

	return result.data.credential
}

/**
 * The signed in employee's passkeys, across every relying party.
 * @param apiUrl Base URL of the API
 * @param token Session token
 * @returns The credentials
 */
export async function passkeyList(apiUrl: string, token: string) {
	const result = await axios.get<{ credentials: WebauthnCredential[] }>(`${apiUrl}/auth/webauthn/credentials`, { headers: { Authorization: `Bearer ${token}` } })

	return result.data.credentials
}

/**
 * Renames a passkey.
 * @param apiUrl Base URL of the API
 * @param token Session token
 * @param id Credential id
 * @param name New label
 */
export async function passkeyRename(apiUrl: string, token: string, id: string, name: string) {
	await axios.patch(`${apiUrl}/auth/webauthn/credentials/${id}`, { name }, { headers: { Authorization: `Bearer ${token}` } })
}

/**
 * Deletes a passkey.
 * @param apiUrl Base URL of the API
 * @param token Session token
 * @param id Credential id
 */
export async function passkeyRemove(apiUrl: string, token: string, id: string) {
	await axios.delete(`${apiUrl}/auth/webauthn/credentials/${id}`, { headers: { Authorization: `Bearer ${token}` } })
}

/**
 * Registers a passkey using an enrollment token, without signing in first. This is the only recovery path
 * for a passkey only employee on the macOS desktop build, where an e-mailed link cannot help because the
 * credential store is private to the application.
 * @param apiUrl Base URL of the API
 * @param enrollToken Token from the enrollment e-mail or SMS
 * @param name Label for the new passkey
 */
export async function passkeyEnroll(apiUrl: string, enrollToken: string, name?: string) {
	const options = await axios.post<WebauthnOptionsResponse<PublicKeyCredentialCreationOptionsJSON>>(`${apiUrl}/auth/webauthn/enroll/options`, { token: enrollToken })
	const response = await startRegistration({ optionsJSON: options.data.options })
	const body = name
		? { token: enrollToken, challengeId: options.data.challengeId, response, name }
		: { token: enrollToken, challengeId: options.data.challengeId, response }

	await axios.post(`${apiUrl}/auth/webauthn/enroll`, body)
}

/**
 * Filters credentials down to the ones usable from a given hostname. The API resolves the relying party per
 * portal: every production portal ('communicatordesktop.dk', 'admin.telecomx.dk', ...) is served by credentials
 * whose rpId is 'telecomx.dk', while the development host is its own relying party ('localhost') - so a
 * 'telecomx.dk' credential cannot be asserted there and must not count.
 * @param credentials All of the employee's credentials
 * @param hostname Hostname the application is served from
 * @returns The credentials that apply here
 */
export function credentialsForThisOrigin(credentials: WebauthnCredential[], hostname: string) {
	const host = hostname.split(':')[0] ?? ''
	const sharedRelyingPartyApplies = host !== 'localhost' && host !== '127.0.0.1'

	return credentials.filter(credential => host === credential.rpId || host.endsWith(`.${credential.rpId}`) || (sharedRelyingPartyApplies && credential.rpId === 'telecomx.dk'))
}

/**
 * How long a declined passkey offer is remembered before the offer is made again - 90 days.
 */
const OFFER_DECLINED_MEMORY_MS = 90 * 24 * 60 * 60 * 1000

/**
 * Builds the localStorage key holding the moment an employee declined the passkey offer.
 * @param employeeId Id of the employee
 * @returns The storage key
 */
function offerDeclinedKey(employeeId: string) {
	return `passkeyOfferDeclinedAt:${employeeId}`
}

/**
 * Whether this employee declined the post login passkey offer recently enough that it should stay hidden.
 * Anything that is not a timestamp inside the memory window counts as not declined, so a corrupted or
 * expired value simply brings the offer back.
 * @param employeeId Id of the employee
 * @returns True when a decline is stored and is younger than 90 days
 */
export function hasDeclinedPasskeyOffer(employeeId: string) {
	return hasRecentStamp(offerDeclinedKey(employeeId), OFFER_DECLINED_MEMORY_MS)
}

/**
 * Whether a timestamp stored under a key is younger than a limit. Anything that is not a timestamp inside the window counts as not
 * stored, so a corrupted, expired or future stamped value (the clock was ahead when it was written) behaves as if nothing were remembered.
 * @param key localStorage key holding a Date.now() value
 * @param maxAgeMs How old the stamp may be
 * @returns True when a valid stamp younger than maxAgeMs is stored
 */
function hasRecentStamp(key: string, maxAgeMs: number) {
	try {
		const stored = localStorage.getItem(key)

		if (!stored) { return false }

		const stampedAt = Number.parseInt(stored, 10)

		if (!Number.isFinite(stampedAt)) { return false }

		const age = Date.now() - stampedAt

		return age >= 0 && age < maxAgeMs
	}
	catch (_) { return false }
}

/**
 * Remembers that this employee declined the post login passkey offer, so it is not offered again for 90 days.
 * @param employeeId Id of the employee
 */
export function setDeclinedPasskeyOffer(employeeId: string) {
	try { localStorage.setItem(offerDeclinedKey(employeeId), Date.now().toString()) }
	catch (_) { /* No storage available - the offer simply comes back on the next login */ }
}

/**
 * Whether the passkeys this application creates are invisible to every other application. That is the case inside the macOS wrapper,
 * where Electron keeps Touch ID credentials in the application's own keychain access group - a passkey made in Chrome or on a phone
 * cannot be used here, and one made here appears nowhere else. In a browser, and in the Windows wrapper where Windows Hello holds the
 * credentials for every application, the API's list of credentials tells the truth about what can be used here.
 * @param embedded True when running inside the desktop wrapper
 * @param platform navigator.platform, injectable for tests - Chromium reports 'MacIntel' on every Mac, Apple silicon included
 * @returns True when only passkeys created in this application can be used here
 */
export function passkeyStoreIsPrivate(embedded: boolean, platform: string = navigator.platform) {
	return embedded && /^Mac/i.test(platform ?? '')
}

/**
 * The longest a NotAllowedError can take and still mean a missing credential rather than a person cancelling. Chromium answers within
 * tens of milliseconds when the platform authenticator holds nothing for the relying party, while a Touch ID prompt takes longer than
 * this just to appear - so a cancellation can never be this fast.
 */
export const MISSING_PASSKEY_MAX_MS = 500

/**
 * Whether a failed passkey login means there is no passkey for this application on this device. Only meaningful inside the wrapper -
 * a browser shows its own UI for a missing credential, and there the same error is the user closing that UI.
 * @param error What the ceremony rejected with
 * @param elapsedMs How long the ceremony took from start to rejection
 * @param embedded True when running inside the desktop wrapper
 * @returns True when the error is a NotAllowedError that came back too fast to have involved the user
 */
export function isMissingLocalPasskeyError(error: unknown, elapsedMs: number, embedded: boolean) {
	return embedded && (error as Error | undefined)?.name === 'NotAllowedError' && elapsedMs < MISSING_PASSKEY_MAX_MS
}

/** How long a failed passkey login keeps forcing the passkey offer on the next password login - a day, so a restart in between does not lose it. */
const LOGIN_FAILED_MEMORY_MS = 24 * 60 * 60 * 1000

/** localStorage key holding the moment a passkey login last failed on this device. Not per employee - nobody is signed in when it happens. */
const LOGIN_FAILED_KEY = 'passkeyLoginFailedAt'

/**
 * Remembers that a passkey login failed on this device, so the next password login offers to create a passkey no matter what was
 * declined or what the API lists - a failed attempt is the clearest sign that no usable passkey exists here.
 */
export function setPasskeyLoginFailed() {
	try { localStorage.setItem(LOGIN_FAILED_KEY, Date.now().toString()) }
	catch (_) { /* No storage available - the offer follows the ordinary rules instead */ }
}

/**
 * Whether a passkey login failed on this device within the last day.
 * @returns True when a failure is stored and still fresh
 */
export function hasRecentPasskeyLoginFailure() {
	return hasRecentStamp(LOGIN_FAILED_KEY, LOGIN_FAILED_MEMORY_MS)
}

/** Forgets a failed passkey login - called once the offer it forced has been shown, or a passkey login has succeeded. */
export function clearPasskeyLoginFailure() {
	try { localStorage.removeItem(LOGIN_FAILED_KEY) }
	catch (_) { /* Nothing stored, nothing to forget */ }
}

/**
 * Builds the localStorage key marking that a passkey was created in this application for an employee.
 * @param employeeId Id of the employee
 * @returns The storage key
 */
function createdHereKey(employeeId: string) {
	return `passkeyCreatedHere:${employeeId}`
}

/**
 * Whether a passkey has been created in this application for this employee. This is the only knowledge available about a private
 * credential store - WebAuthn has no way to list what an authenticator holds.
 * @param employeeId Id of the employee
 * @returns True when the marker is set
 */
export function hasPasskeyCreatedHere(employeeId: string) {
	try { return localStorage.getItem(createdHereKey(employeeId)) !== null }
	catch (_) { return false }
}

/**
 * Marks that a passkey exists in this application for this employee - set when one is created here, and when a passkey login succeeds here.
 * @param employeeId Id of the employee
 */
export function setPasskeyCreatedHere(employeeId: string) {
	try { localStorage.setItem(createdHereKey(employeeId), Date.now().toString()) }
	catch (_) { /* No storage available - the offer simply comes back on the next login */ }
}

/**
 * Removes the marker, so the offer returns. Called when the employee removes a passkey - which one lived here cannot be told, and an
 * unnecessary offer is cheaper than a missing one.
 * @param employeeId Id of the employee
 */
export function clearPasskeyCreatedHere(employeeId: string) {
	try { localStorage.removeItem(createdHereKey(employeeId)) }
	catch (_) { /* Nothing stored, nothing to forget */ }
}

/** What the post login passkey offer decision needs to know. */
export interface PasskeyOfferInput {
	/** Id of the employee who just signed in */
	employeeId: string
	/** True when only passkeys created in this application can be used here - see passkeyStoreIsPrivate() */
	privateStore: boolean
	/** The employee's credentials as the API lists them - only consulted when the store is shared */
	credentials: WebauthnCredential[]
	/** Hostname the application is served from */
	hostname: string
}

/**
 * Whether to offer creating a passkey after a password login. A recent failed passkey login always offers - it proves nothing usable
 * exists here. Otherwise a recent decline wins, and then the store decides: a private store offers until a passkey has been created in
 * this application, a shared store offers until the API lists a credential that works from this origin.
 * @param input See PasskeyOfferInput
 * @returns True when the offer should be shown
 */
export function shouldOfferPasskey({ employeeId, privateStore, credentials, hostname }: PasskeyOfferInput) {
	if (hasRecentPasskeyLoginFailure()) { return true }
	if (hasDeclinedPasskeyOffer(employeeId)) { return false }
	if (privateStore) { return !hasPasskeyCreatedHere(employeeId) }

	return credentialsForThisOrigin(credentials, hostname).length === 0
}

/** An error thrown by a ceremony run through timedCeremony() carries how long the ceremony took. */
interface TimedCeremonyError {
	ceremonyMs?: number
}

/**
 * Runs a WebAuthn ceremony and, when it rejects, tags the error with how long the ceremony itself took. The API calls around the
 * ceremony are left out on purpose - a slow network must never make a missing credential look like a cancellation.
 * @param ceremony The ceremony to run
 * @returns Whatever the ceremony resolves with
 */
async function timedCeremony<T>(ceremony: () => Promise<T>) {
	const startedAt = Date.now()

	try { return await ceremony() }
	catch (error) {
		if (error && typeof error === 'object') { (error as TimedCeremonyError).ceremonyMs = Date.now() - startedAt }
		throw error
	}
}

/**
 * How long the ceremony behind a failed passkey login took. An error that never reached a ceremony, such as the options call
 * failing, counts as having taken forever, so it can never pass for a missing credential.
 * @param error What passkeyLogin() rejected with
 * @returns Milliseconds, or Infinity
 */
export function ceremonyDuration(error: unknown) {
	const ms = (error as TimedCeremonyError | undefined)?.ceremonyMs

	return typeof ms === 'number' ? ms : Number.POSITIVE_INFINITY
}

/**
 * Whether a credential the API lists cannot be used from here. Only a private store (the macOS wrapper) has such credentials: one that
 * syncs - backed up, or eligible to be - lives in a browser's or a phone's store and never in this application's Secure Enclave group.
 * A device bound credential is left alone, since it may well be the one created here; WebAuthn offers no way to tell.
 * @param credential A credential as the API lists it
 * @param privateStore True when only passkeys created in this application can be used here - see passkeyStoreIsPrivate()
 * @returns True when the credential should be marked as unusable in this application
 */
export function passkeyUnusableHere(credential: WebauthnCredential, privateStore: boolean) {
	return privateStore && (credential.backedUp || credential.deviceType === 'multiDevice')
}
